import { ChangedEventSchema, StreamTicketSchema } from '@contracts/schemas/realtime.schema';
import type { LiveStream, LiveStreamHandlers } from '@/application/ports/LiveStream';
import { env } from '@/shared/config/env';
import { request } from '../http/client';

/** What this needs of an EventSource, so a test can hand it a fake one. */
export interface EventSourceLike {
  addEventListener(type: string, listener: (event: MessageEvent<string>) => void): void;
  onerror: ((event: Event) => void) | null;
  close(): void;
}

export type OpenEventSource = (url: string) => EventSourceLike;

/** How long to wait before each reconnect in a row, in milliseconds. */
export const RECONNECT_DELAYS_MS = [2_000, 5_000, 15_000, 30_000, 60_000] as const;

/** A stream that ran this long and then ended was closed on purpose, and is reopened at once. */
const LONG_LIVED_MS = 60_000;

const browserEventSource: OpenEventSource | null =
  typeof EventSource === 'undefined' ? null : (url) => new EventSource(url);

/**
 * The live event stream over Server Sent Events. Section 24 of the review of
 * 2 October 2026.
 *
 * EventSource cannot send an Authorization header, so each connection first
 * exchanges the access token for a single use ticket. That is also why the
 * browser's own reconnect is not used: it would reuse a spent ticket. On any
 * error the source is closed, the status goes down so the screens poll every
 * fifteen seconds again, and a fresh ticket is asked for after a backoff. A
 * stream the server closed after a long life (it ends each one after fifteen
 * minutes) is reopened straight away.
 *
 * Where there is no EventSource at all (the test environment, an old browser)
 * the status simply stays down and polling does the work, as it always has.
 */
export class SseLiveStream implements LiveStream {
  constructor(
    private readonly openSource: OpenEventSource | null = browserEventSource,
    private readonly now: () => number = Date.now,
    private readonly delays: readonly number[] = RECONNECT_DELAYS_MS,
  ) {}

  connect(handlers: LiveStreamHandlers): () => void {
    const openSource = this.openSource;
    if (openSource === null) {
      handlers.onStatus('down');
      return () => undefined;
    }

    let stopped = false;
    let source: EventSourceLike | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let failures = 0;
    let openedAt: number | null = null;

    const schedule = () => {
      if (stopped) return;
      const ranLong = openedAt !== null && this.now() - openedAt >= LONG_LIVED_MS;
      const index = Math.min(failures, this.delays.length - 1);
      const delay = ranLong ? 0 : (this.delays[index] ?? 60_000);
      failures = ranLong ? 0 : failures + 1;
      openedAt = null;
      timer = setTimeout(() => void open(), delay);
    };

    const open = async () => {
      timer = null;
      if (stopped) return;
      handlers.onStatus('connecting');

      let ticket: string;
      try {
        ticket = (await request('/events/ticket', { method: 'POST', schema: StreamTicketSchema }))
          .ticket;
      } catch {
        if (stopped) return;
        handlers.onStatus('down');
        schedule();
        return;
      }
      if (stopped) return;

      const opened = openSource(
        `${env.apiBaseUrl}/events?ticket=${encodeURIComponent(ticket)}`,
      );
      source = opened;

      opened.addEventListener('connected', () => {
        failures = 0;
        openedAt = this.now();
        handlers.onStatus('open');
      });
      opened.addEventListener('changed', (event) => {
        // The ids are a hint. An event that does not parse still means
        // something changed, so it refetches everything live.
        let parsed: unknown = null;
        try {
          parsed = JSON.parse(event.data);
        } catch {
          parsed = null;
        }
        const change = ChangedEventSchema.safeParse(parsed);
        handlers.onChanged(
          change.success
            ? { kitIds: change.data.kitIds, jobOrderNos: change.data.jobOrderNos }
            : { kitIds: [], jobOrderNos: [] },
        );
      });
      opened.addEventListener('notification', () => handlers.onNotification());
      opened.addEventListener('session', () => handlers.onSession());
      opened.onerror = () => {
        opened.close();
        if (source === opened) source = null;
        if (stopped) return;
        handlers.onStatus('down');
        schedule();
      };
    };

    void open();

    return () => {
      stopped = true;
      if (timer !== null) clearTimeout(timer);
      source?.close();
      source = null;
    };
  }
}
